import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bot } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { usd } from "@/components/dash/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELDS = [["stake_pct", "Bet this % of my balance"], ["max_bet", "Never bet more than ($)"], ["daily_loss_limit", "Stop for the day after losing ($)"], ["max_trades_per_day", "Max trades per day"],
  ["take_profit_pct", "Sell when up (%)"], ["stop_loss_pct", "Sell when down (%, negative)"], ["exit_before_close_sec", "Sell this many seconds before close"]];

export const LiveTradingCard = () => {
  const [s, setS] = useState(null);
  const [form, setForm] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [phrase, setPhrase] = useState("");
  const load = useCallback(() => axios.get(`${API}/live/state`).then((r) => { setS(r.data); setForm((f) => f || r.data.config); }).catch(() => {}), []);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  if (!s || !form) return null;
  const save = async (extra = {}) => {
    try {
      const body = { pin: localStorage.getItem("owner_pin"), ...Object.fromEntries(FIELDS.map(([k]) => [k, Number(form[k])])), ...extra };
      await axios.post(`${API}/live/config`, body);
      toast.success(extra.enabled === true ? "Trading bot is ON" : extra.enabled === false ? "Trading bot is OFF" : "Limits saved");
      setConfirm(false); setPhrase(""); load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Couldn't save"); }
  };
  const on = s.config.enabled;
  return (
    <section className={`glass p-5 sm:p-6 ${on ? "ring-1 ring-emerald-400/50" : ""}`} data-testid="live-trading-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-head font-bold text-lg flex items-center gap-2"><Bot className="w-5 h-5 text-[var(--cyan)]" /> Trading bot (real money)</h2>
          <p className="text-xs text-slate-400 mt-1 max-w-xl">Runs on the server 24/7 — even when the app is closed. It trades the BTC 15-min markets on your Polymarket US account <b>only</b> when the signal says BUY UP or BUY DOWN, betting a % of your balance, max one buy per 15-min market, and sells using the rules below. Telegram tells you every trade. OFF by default.</p>
        </div>
        <Switch checked={on} onCheckedChange={(v) => (v ? setConfirm(true) : save({ enabled: false }))} data-testid="live-trading-switch" />
      </div>
      <div className={`mt-3 text-sm font-semibold ${on ? "text-emerald-300" : "text-slate-400"}`} data-testid="live-trading-status">
        {on ? "ON — watching every market, waiting for a validated BUY signal" : "OFF — no real trades will be placed"} · today: {s.today.trades} trades, {usd(s.today.realized_pnl)}
      </div>
      <p className="text-[11px] text-amber-300/90 mt-1" data-testid="live-trading-note">The signal only unlocks after the track record proves itself (30+ scored signals with positive returns), so the bot may not trade for a while. That's on purpose.</p>
      {s.last_error && <div className="text-xs text-red-400 mt-1" data-testid="live-trading-error">Last problem: {s.last_error.error}</div>}
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {FIELDS.map(([k, label]) => (
          <label key={k} className="block text-xs text-slate-400">{label}
            <Input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-[var(--bg)] border-[var(--line)] h-10 font-num" data-testid={`live-${k}`} />
          </label>
        ))}
      </div>
      <button onClick={() => save()} className="btn-ghost mt-3" data-testid="live-save-btn">Save limits</button>
      <div className="mt-4 text-xs" data-testid="live-trades">
        {s.recent.length ? s.recent.map((t, i) => (
          <div key={i} className="flex justify-between border-b border-[var(--line)] py-1 font-num">
            <span>{new Date(t.opened_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {t.side} · {t.contracts} contracts</span>
            <span className={t.pnl > 0 ? "text-emerald-300" : t.pnl < 0 ? "text-red-300" : "text-slate-400"}>{t.status}{t.pnl != null ? ` ${usd(t.pnl)}` : ""}</span>
          </div>
        )) : <div className="text-slate-500">No real trades yet.</div>}
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="bg-[var(--card)] border-[var(--line)] text-slate-100 max-w-sm" data-testid="live-confirm-dialog">
          <DialogHeader><DialogTitle>Turn on real-money trading?</DialogTitle>
            <DialogDescription className="text-slate-400">The bot will spend up to {usd(Number(form.max_bet))} per bet from your Polymarket US balance, up to {form.max_trades_per_day} times a day. You can lose that money. Type I UNDERSTAND to continue.</DialogDescription></DialogHeader>
          <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="I UNDERSTAND" className="bg-[var(--bg)] border-[var(--line)] h-11" data-testid="live-confirm-input" />
          <button onClick={() => save({ enabled: true, confirm: phrase })} className="py-3 rounded-full bg-emerald-400 text-[#04130D] font-bold" data-testid="live-confirm-btn">Turn ON</button>
        </DialogContent>
      </Dialog>
    </section>
  );
};
